import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useBreakpoint } from '@ast/magma/breakpoints';

import { PageLayout } from '@app/common/components/PageLayout';
import {
  SelectableGroupFieldLocator,
} from '@app/common/configurable-wizards/fields/SelectableGroupFieldInfo';
import {
  SingleChoiceRadioButtonsFieldInfoLocator,
} from '@app/common/configurable-wizards/fields/SingleChoice/RadioButtons/SingleChoiceRadioButtonsField';

import { PageURL } from '@app/core/widgets/pages';

import { NotifierWrapper } from '@app/widgets/contacts-and-settings/components/NotifierWrapper';
import { useSettingFieldsContext } from '@app/widgets/contacts-and-settings/contexts/SettingFieldsContext';
import {
  AddressFieldsListLocator,
} from '@app/widgets/contacts-and-settings/fields/Address/AddressFieldsList';
import {
  AddressFieldsListItemLocator,
} from '@app/widgets/contacts-and-settings/fields/Address/AddressFieldsListItem';
import {
  MemberAddressFieldsListLocator,
} from '@app/widgets/contacts-and-settings/fields/Address/MemberAddressFieldsList';
import {
  DisplayEmailFieldInfoGroupLocator,
} from '@app/widgets/contacts-and-settings/fields/Email/EmailFieldList';
import {
  PhoneFieldsListLocator,
} from '@app/widgets/contacts-and-settings/fields/Phone/PhoneFieldsList';
import {
  PhoneFieldsListItemLocator,
} from '@app/widgets/contacts-and-settings/fields/Phone/PhoneFieldsListItem';
import {
  ProfileAndSecurityFieldsListLocator,
} from '@app/widgets/contacts-and-settings/fields/ProfileAndSecurity/ProfileAndSecurityFieldsList';
import { useUserSettingsLocator } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsLocator';
import {
  useGetUserContactsSettingsQuery,
} from '@app/widgets/contacts-and-settings/queries/queryTyping/getUserContactSettings';
import { UrlParams } from '@app/widgets/contacts-and-settings/types';
import { RenderSection } from '@app/widgets/contacts-and-settings/utils/settingsSections';

export const FieldsList: React.FC = () => {
  const { sectionId } = useParams<UrlParams>();
  const { sections } = useSettingFieldsContext();
  const isMobile = useBreakpoint().device === 'mobile';

  // preload data for smooth transitions
  useGetUserContactsSettingsQuery();

  const locator = useUserSettingsLocator([
    // profile and security
    ProfileAndSecurityFieldsListLocator,

    // contacts
    DisplayEmailFieldInfoGroupLocator,
    SingleChoiceRadioButtonsFieldInfoLocator,
    PhoneFieldsListLocator,
    PhoneFieldsListItemLocator,

    MemberAddressFieldsListLocator,
    AddressFieldsListLocator,
    AddressFieldsListItemLocator,

    // common
    SelectableGroupFieldLocator,
  ]);

  const { title, fields } = useMemo<RenderSection>(
    () => sections.find(({ id }) => id === sectionId) || {} as RenderSection,
    [sections, sectionId],
  );

  const fieldsList = useMemo(() => fields?.map((field) => {
    const Component = locator(field);

    return (
      <Component
        key={field.id}
        parent=""
        field={field}
        locator={locator}
      />
    );
  }), [fields]);

  return (
    <PageLayout
      title={title}
      {...(isMobile ? {
        backRoute: PageURL.CONTACTS_AND_SETTINGS,
      } : {})}
    >
      <NotifierWrapper />
      {fieldsList}
    </PageLayout>
  );
};
